import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { API_URL } from '@core/config/api-url.token';
import { API_ENDPOINTS } from '@shared/constants/api-endpoints.const';
import { Observable } from 'rxjs';
import { AppointmentDto, PagedAppointmentsDto } from '../../dtos/appointment.dto';
import { DepartmentDto } from '../../dtos/department.dto';
import { PagedExternalResourcesDto } from '../../dtos/external-resource.dto';
import { PagedProfessionalsDto } from '../../dtos/professional.dto';
import { PagedShiftsDto } from '../../dtos/shift.dto';

export interface AppointmentsQueryParams {
  readonly startFrom: string;
  readonly startTo: string;
  readonly clientId?: string;
  readonly patientId?: string;
  readonly departmentId?: string;
  readonly page: number;
  readonly pageSize: number;
}

@Injectable()
export class AppointmentsRemoteDatasource {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = inject(API_URL);

  public list(query: AppointmentsQueryParams): Observable<PagedAppointmentsDto> {
    const params: Record<string, string | number> = {
      startFrom: query.startFrom,
      startTo: query.startTo,
      page: query.page,
      pageSize: query.pageSize,
    };
    if (query.clientId) params['clientId'] = query.clientId;
    if (query.patientId) params['patientId'] = query.patientId;
    if (query.departmentId) params['departmentId'] = query.departmentId;

    return this.http.get<PagedAppointmentsDto>(`${this.apiUrl}${API_ENDPOINTS.appointments.list}`, { params });
  }

  public getById(id: string): Observable<AppointmentDto> {
    return this.http.get<AppointmentDto>(`${this.apiUrl}${API_ENDPOINTS.appointments.byId(id)}`);
  }

  public create(payload: Record<string, unknown>): Observable<AppointmentDto> {
    return this.http.post<AppointmentDto>(`${this.apiUrl}${API_ENDPOINTS.appointments.list}`, payload);
  }

  public update(id: string, payload: Record<string, unknown>): Observable<AppointmentDto> {
    return this.http.patch<AppointmentDto>(`${this.apiUrl}${API_ENDPOINTS.appointments.byId(id)}`, payload);
  }

  public delete(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}${API_ENDPOINTS.appointments.byId(id)}`);
  }

  public listProfessionals(): Observable<PagedProfessionalsDto> {
    return this.http.get<PagedProfessionalsDto>(`${this.apiUrl}${API_ENDPOINTS.appointments.professionals}`);
  }

  public listReasonTypes(): Observable<PagedExternalResourcesDto> {
    return this.http.get<PagedExternalResourcesDto>(`${this.apiUrl}${API_ENDPOINTS.appointments.reasonTypes}`);
  }

  // Cross-controller on purpose -- departments are an organization-scoped concept (unified
  // DepartmentsController), not an appointments-scoped one, but this is the only feature that
  // currently needs them for a lookup dropdown (ProVet requires `department` on appointment
  // creation). Unpaged array response, unlike the other lookups on this datasource.
  public listDepartments(organizationId: number): Observable<readonly DepartmentDto[]> {
    return this.http.get<readonly DepartmentDto[]>(`${this.apiUrl}${API_ENDPOINTS.departments.list(organizationId)}`);
  }

  // Lives on AppointmentsSchedulingController (route base `api/veterinary/appointments-scheduling`),
  // not AppointmentsController -- it sits alongside the existing generic shifts/shift-types passthrough
  // endpoints there. Returns a paged Items/TotalCount envelope like the other lookups above; a single
  // page at DEFAULT_PAGE_SIZE is assumed sufficient for a month's worth of shifts.
  public getProfessionalShifts(from: string, to: string): Observable<PagedShiftsDto> {
    return this.http.get<PagedShiftsDto>(
      `${this.apiUrl}${API_ENDPOINTS.appointments.professionalShifts}`,
      { params: { from, to, page: 1, pageSize: 200 } },
    );
  }
}
